<template>
    <v-expansion-panels v-model="filtersExpanded" flat>
        <v-expansion-panel>
            <v-expansion-panel-title v-if="isMobile" class="pl-1 width-auto text-primary text-capitalize">
                {{ $t('filters') }}
            </v-expansion-panel-title>
            <v-expansion-panel-text>
                <v-row>
                    <v-col cols="12">
                        <v-radio-group
                            v-model="quickFilter"
                            density="compact"
                            hide-details
                            :inline="!isMobile"
                            @update:model-value="onQuickFilterChange"
                        >
                            <v-radio class="text-capitalize me-2" :label="$t('all')" value="all" />
                            <v-radio :label="$t('future_trips')" value="future" class="me-2" />
                            <v-radio :label="$t('past_trips')" value="past" class="me-2" />
                        </v-radio-group>
                    </v-col>
                </v-row>
                <v-row class="py-4">
                    <v-col md="8" cols="12" class="filters-form">
                        <v-row>
                            <v-col md="8" cols="12">
                                <order-service-daterange
                                    :start-date-label="$t('start_date_from')"
                                    :end-date-label="$t('end_date_to')"
                                    :outlined="false"
                                    :single-line="false"
                                    :no-title="true"
                                    dense
                                    clearable
                                    :_start-date="filters.dateStart"
                                    :_end-date="filters.dateEnd"
                                    @change-start-date="filters = {...filters, dateStart: $event}"
                                    @change-end-date="filters = {...filters, dateEnd: $event}"
                                />
                            </v-col>
                            <v-col cols="6" md="4">
                                <v-select
                                    v-model="filters.serviceStatus"
                                    :items="serviceStatuses"
                                    :label="$t('status_by_services')"
                                    clearable
                                    :item-title="'text'"
                                    :item-value="'value'"
                                    density="compact"
                                    hide-details
                                />
                            </v-col>
                            <v-col cols="6" md="4">
                                <v-select
                                    v-model="filters.serviceType"
                                    :items="serviceTypes"
                                    :label="$t('service_types')"
                                    clearable
                                    :item-title="'text'"
                                    :item-value="'value'"
                                    density="compact"
                                    hide-details
                                />
                            </v-col>
                            <v-col cols="6" md="4">
                                <v-select
                                    v-model="filters.programType"
                                    :items="programTypes"
                                    :label="$t('shared_or_individual')"
                                    clearable
                                    :item-title="'text'"
                                    :item-value="'value'"
                                    density="compact"
                                    hide-details
                                />
                            </v-col>
                            <v-col cols="6" md="4">
                                <v-text-field
                                    v-model="filters.serviceName"
                                    :label="$t('service_name')"
                                    density="compact"
                                    hide-details
                                />
                            </v-col>
                            <v-col cols="6" md="4">
                                <person-select-autocomplete
                                    v-model="filters.guideId"
                                    :show-icon="false"
                                    disable-persistent-placeholder
                                    item-value="personId"
                                    :label="$t('roles.guide')"
                                    :role="'guide'"
                                    dense
                                    :rules="[]"
                                />
                            </v-col>
                            <v-col cols="6" md="4">
                                <person-select-autocomplete
                                    v-model="filters.driverId"
                                    :show-icon="false"
                                    dense
                                    item-value="personId"
                                    :role="'driver'"
                                    disable-persistent-placeholder
                                    :label="$t('roles.driver')"
                                    :rules="[]"
                                />
                            </v-col>
                            <v-col cols="6" md="4">
                                <v-text-field
                                    v-model="filters.vehicle"
                                    :label="$t('vehicle')"
                                    density="compact"
                                    hide-details
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                    <v-col md="4" cols="12">
                        <v-row>
                            <v-col cols="6">
                                <v-btn
                                    block
                                    :color="isSomeFiltersSelected ? 'primary' : 'white'"
                                    size="small"
                                    @click="submitFilters"
                                >
                                    {{ $t('filter') }}
                                </v-btn>
                            </v-col>
                            <v-col cols="6">
                                <v-btn color="white" block size="small" @click="resetFilters">
                                    {{ $t('reset') }}
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-expansion-panel-text>
        </v-expansion-panel>
    </v-expansion-panels>
</template>

<script lang="ts">
    import {Component, Emit, VModel, Vue, Watch} from 'vue-facing-decorator'
    import PrivateClientAndCompaniesAutocomplete from '~/components/search/forms/PrivateClientAndCompaniesAutocomplete.vue'
    import OrderServiceDaterange from '~/components/account/filters/OrderServiceDaterange.vue'
    import PersonSelectAutocomplete from '@/components/search/forms/PersonSelectAutocomplete.vue'

    @Component({
        components: {
            OrderServiceDaterange,
            PrivateClientAndCompaniesAutocomplete,
            PersonSelectAutocomplete,
        },
    })
    export class SchedulerFilters extends Vue {
        @VModel({default: {}}) filters

        filtersExpanded = 0
        quickFilter = null

        @Watch('filters', {deep: true})
        onFiltersChange() {
            Object.keys(this.filters).forEach(key =>
                this.filters[key] === undefined || this.filters[key] === null || this.filters[key] === ''
                    ? delete this.filters[key] // eslint-disable-line @typescript-eslint/no-dynamic-delete
                    : {}
            )
            if (this.filters.dateStart === undefined && this.filters.dateEnd === undefined) {
                this.quickFilter = 'all'
            } else if (this.filters.dateEnd === undefined && this.filters.dateStart >= new Date()) {
                this.quickFilter = 'future'
            } else if (this.filters.dateStart === undefined && this.filters.dateEnd <= new Date()) {
                this.quickFilter = 'past'
            } else {
                this.quickFilter = null
            }
        }

        mounted() {
            if (this.isMobile) {
                this.filtersExpanded = null
            }
        }

        @Emit()
        async resetFilters() {
            this.filters = {}
        }

        @Emit()
        async submitFilters() {}

        async onQuickFilterChange() {
            switch (this.quickFilter) {
                case 'all':
                    delete this.filters.dateStart
                    delete this.filters.dateEnd

                    this.filters = {
                        ...this.filters,
                    }

                    break
                case 'future':
                    delete this.filters.dateEnd

                    this.filters = {
                        ...this.filters,
                        dateStart: new Date(),
                    }

                    break
                case 'past':
                    delete this.filters.dateStart
                    this.filters = {
                        ...this.filters,
                        dateEnd: new Date(),
                    }

                    break
                default:
                    break
            }

            await this.submitFilters()
        }

        get isSomeFiltersSelected() {
            return Object.keys(this.filters).length !== 0
        }

        get serviceStatuses() {
            return ['NEW', 'IN_PROGRESS', 'DONE', 'CANCELLED'].map(value => ({
                value,
                text: this.$t(`orderStatus.${value.split(' ').join('_')}`),
            }))
        }

        get serviceTypes() {
            const serviceTypes = ['TRANSFER', 'EXCURSION']

            return serviceTypes.map(value => ({value, text: this.$t(`serviceType.${value}`)}))
        }

        get programTypes() {
            return [
                {
                    value: 'GROUP',
                    text: this.$t('filters_transferType.GROUP'),
                },
                {
                    value: 'INDIVIDUAL',
                    text: this.$t('individual'),
                },
            ]
            return ['GROUP', 'INDIVIDUAL'].map(value => ({value, text: this.$t(`${value.toLowerCase()}`)}))
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }
    }

    export default SchedulerFilters
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';

    :deep(div.v-expansion-panel-content__wrap) {
        padding: 0 !important;
    }

    @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
        :deep(.v-expansion-panel-header) {
            display: none;
        }
    }
</style>
